import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function TechnicalTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data
  const colors = getColorClasses(color)

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in', margin: '0 auto' }}>
          {/* Tech Header with Code-like Design */}
          <div className={`bg-gradient-to-r ${colors.headerBg} ${colors.headerText} p-8`}>
            <div className={`font-mono text-sm ${colors.accentText} mb-2`}>$ whoami</div>
            <h1 className={`text-4xl font-bold mb-2 ${colors.sectionTitle}`}>{personalInfo.fullName || 'YOUR NAME'}</h1>
            <div className={`${colors.accentText} text-lg mb-4`}>{'<'}{personalInfo.email || 'your.email@domain.com'}{'>'}</div>
            <div className="flex flex-wrap gap-4 text-sm">
              {personalInfo.phone && (
                <span className="flex items-center">
                  <span className={`${colors.accentText} mr-2`}>●</span>{personalInfo.phone}
                </span>
              )}
              {personalInfo.github && (
                <span className="flex items-center">
                  <span className={`${colors.accentText} mr-2`}>●</span>{personalInfo.github}
                </span>
              )}
              {personalInfo.linkedin && (
                <span className="flex items-center">
                  <span className={`${colors.accentText} mr-2`}>●</span>{personalInfo.linkedin}
                </span>
              )}
            </div>
          </div>

          <div className="p-8">
            {/* Summary with Code Style */}
            {summary && (
              <div className={`mb-6 border-l-4 ${colors.accentBorder} pl-4`}>
                <div className={`font-mono text-xs ${colors.accentText} mb-2`}>// {labels.professionalSummary}</div>
                <p className="text-gray-700 leading-relaxed">{summary}</p>
              </div>
            )}

            {/* Skills Grid - Prominent */}
            {skills && skills.length > 0 && (
              <div className="mb-6">
                <div className={`font-mono text-xs ${colors.accentText} mb-3`}>// {labels.skills}</div>
                <div className="grid grid-cols-2 gap-4">
                  {skills.map((skillGroup: any, idx: number) => (
                    <div key={idx} className="border border-gray-200 rounded p-3">
                      <div className={`text-sm font-bold ${colors.sectionTitle} mb-2`}>{skillGroup.category}</div>
                      <div className="flex flex-wrap gap-2">
                        {skillGroup.skills?.map((skill: string, i: number) => (
                          <span key={i} className={`text-xs ${colors.badgeBg} ${colors.badgeText} px-2 py-1 rounded font-mono`}>
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Experience */}
            {experience && experience.length > 0 && (
              <div className="mb-6">
                <div className={`font-mono text-xs ${colors.accentText} mb-3`}>// {labels.workExperience}</div>
                <div className="space-y-4">
                  {experience.map((exp: any) => (
                    <div key={exp.id} className={`border-l-2 ${colors.timelineBorder} pl-4`}>
                      <div className="flex justify-between items-start mb-1">
                        <h3 className={`text-base font-bold ${colors.sectionTitle}`}>{exp.jobTitle}</h3>
                        <span className="text-xs text-gray-600 font-mono">{exp.startDate} → {exp.endDate || labels.present}</span>
                      </div>
                      <div className={`text-sm ${colors.accentText} font-semibold mb-2`}>{exp.company}</div>
                      <p className="text-sm text-gray-600 whitespace-pre-line">{exp.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Projects */}
            {projects && projects.length > 0 && (
              <div className="mb-6">
                <div className={`font-mono text-xs ${colors.accentText} mb-3`}>// {labels.projects}</div>
                <div className="grid grid-cols-2 gap-4">
                  {projects.map((project: any) => (
                    <div key={project.id} className="border border-gray-200 rounded p-3">
                      <h3 className={`text-sm font-bold ${colors.sectionTitle} mb-1`}>{project.name}</h3>
                      <p className="text-xs text-gray-600 mb-2">{project.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {project.technologies?.map((tech: string, i: number) => (
                          <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  )
}
